import { React, AppForms, defaultDateFormat, Settings, navigation } from '@/config'
import { APIClient } from '@/services'
import { useMemo, useState } from '@codeleap/hooks'
import { Button, Checkbox, Text, TextInput, View, AuthFormComponents, OnboardingModal } from '@/components'
import { DatePicker, useKeydown } from '@codeleap/web'
import { appStatusStore } from '@/stores'
import { useLingui } from '@lingui/react/macro'
import dayjs from 'dayjs'

type SignupFormProps = {
  title?: string
}

export const SignupForm = ({ title = null }: SignupFormProps) => {
  const [signUpSource, setSignUpSource] = useState('email')

  const form = useMemo(() => signUpSource === 'email' ? AppForms.signup : AppForms.socialSignup, [signUpSource])

  const isValid = form.use(f => f.isValid)

  const handleSocial = APIClient.Session.useSocialLogin((userSocial, provider) => {
    if (userSocial) {
      appStatusStore.set('loading')
      form.setValues(userSocial)
      setSignUpSource(provider)
      handleSubmit(true, { ...userSocial, birthday: form.values?.birthday })
    }
  }, 'signup')

  const { t } = useLingui()

  const signup = APIClient.Session.useSignup()

  const isSocial = signUpSource !== 'email'

  const handleSubmit = async (isSocialSignup = false, userValues = null) => {
    const values = (userValues ?? form?.values)

    const data = {
      ...values,
      birthday: dayjs(values.birthday).format(defaultDateFormat),
    }

    const result = await signup(data, signUpSource as any)

    if (!!result?.user) {
      if (isSocialSignup || !Settings.AppConfig.emailVerificationEnabled) {
        navigation.navigate('Home')
        return OnboardingModal.toggle()
      }

      navigation.navigate('Auth.VerifyEmail')
    }
  }

  useKeydown('Enter', () => {
    const isInvalidSubmit = !isValid
    if (isInvalidSubmit) return
    handleSubmit?.()
  }, [isValid])

  return (
    <View style={['column', 'fullWidth']}>
      <Text
        style={['h1', 'extraBold', 'marginBottom:4']}
        text={title || t`Get Started`}
      />
      <Text style={['p1', 'marginBottom:4', 'marginTop:-3']} text={t`Create your account now` + ' '} />
      <View style={['flex', 'column', 'gap:4', 'fullWidth']}>
        <View style={['column', 'gap:1']}>
          <TextInput {...form.register('first_name')} />
          <TextInput {...form.register('last_name')} />
          <TextInput
            {...form.register('email')}
            leftIcon={{ icon: 'mail' }}
            disabled={isSocial}
          />
          <DatePicker {...form.register('birthday')} />
          {
            isSocial ? null : (
              <>
                <TextInput
                  {...form.register('password')}
                  leftIcon={{ name: 'key' }}
                  visibilityToggle
                />
                <TextInput
                  {...form.register('repeatPassword')}
                  leftIcon={{ name: 'key' }}
                  visibilityToggle
                />
              </>
            )
          }
          <View style={['column', 'gap:2']}>
            <View>
              <Checkbox
                {...form.register('terms')}
                style={['left']}
              />
            </View>
          </View>
        </View>

        <AuthFormComponents.SocialProviders onPress={handleSocial} />

        <View style={['column', 'gap:2']}>
          <Button
            onPress={handleSubmit}
            disabled={!isValid}
            debugName={`submit Signup`}
            text={t`Create account`}
            style={['marginHorizontal:auto', 'fullWidth', 'large', 'paddingHorizontal:2', !isValid && 'outline']}
          />

          <AuthFormComponents.RedirectLink
            route={'Auth.Login'}
            text={t`Have an account?`}
            textAction={t`Log in`}
          />
        </View>
      </View>
    </View>
  )
}
